import { useEffect, useState } from 'react';

export const SCROLL_UP = 'up';
export const SCROLL_DOWN = 'down';

const throttle = (fn: () => void, wait: number): (() => void) => {
  let time = Date.now();
  return function () {
    if (time + wait - Date.now() < 0) {
      fn();
      time = Date.now();
    }
  };
};

const TIMEOUT_EVENT_LISTENER = 300;

export const useScrollDirection = ({
  initialDirection = SCROLL_DOWN,
  offsetY = 64,
  disabled = false,
}: {
  initialDirection: string;
  offsetY: number;
  disabled?: boolean;
}): {
  direction: null | string;
  listenerActive: boolean;
} => {
  const [scrollDir, setScrollDir] = useState<null | string>(initialDirection);
  const [listenerActive, setListenerActive] = useState(false);

  useEffect(() => {
    if (disabled) {
      setScrollDir(null);
      return;
    }

    let lastScrollY = window.pageYOffset;
    let ticking = false;

    const updateScrollDir = (): void => {
      const scrollY = window.pageYOffset;

      if (scrollY < offsetY) {
        setListenerActive(false);
      } else {
        setScrollDir(scrollY > lastScrollY ? SCROLL_DOWN : SCROLL_UP);
        setListenerActive(true);
      }

      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const onScroll = (): void => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttle(onScroll, TIMEOUT_EVENT_LISTENER));

    return () => window.removeEventListener('scroll', onScroll);
  }, [initialDirection, offsetY, disabled]);

  return {
    direction: scrollDir,
    listenerActive,
  };
};
